import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

// Component Functions
import * as ComponentFunctions from './PractitionerConsumptionSyncProcess.Functions';
import {
    Link,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
} from '@fluentui/react-components';
import {
    ArrowSyncRegular,
    ArrowSyncDismissRegular,
    CheckmarkCircleRegular,
    ClockRegular,
} from '@fluentui/react-icons';

import './PractitionerConsumptionSyncProcess.Styles.css';

type StatusVariant = 'idle' | 'running' | 'success' | 'error';

interface StatusDescriptor {
    variant: StatusVariant;
    eyebrow: string;
    title: string;
    pillLabel: string;
    timestamp?: string;
    actionLabel: string;
    actionDisabled: boolean;
    icon: React.ReactNode;
}

const formatTimestamp = (date: string | Date | undefined) =>
    date ? moment(date).format('DD/MM/YYYY HH:mm') : undefined;

const PractitionerConsumptionSyncProcess = (props: ComponentFunctions.Props) => {
    useEffect(() => {
        if (props.practitioner?.userId) {
            props.GetPractitionerById(props.PractitionerId);
        }
    }, [props.practitioner?.userId !== undefined]);

    useEffect(() => {
        if (props.practitioner && props.practitioner?.lastConsumptionSyncProcess === undefined) {
            props.GetProtocolsByPractitionerId(props.practitioner?.userId);
            props.GetLastPractitionerConsumptionSyncProcessByPractitionerId(props.practitioner?.userId);
        }
    }, [props.practitioner?.userId !== undefined]);

    // Poll while running
    useEffect(() => {
        if (props.CurrentProcess?.status === 1 || props.CurrentProcess?.status === 2) {
            const interval = setInterval(() => {
                props.GetLastPractitionerConsumptionSyncProcessByPractitionerId(props.PractitionerId);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [props.practitioner?.userId !== undefined, props.CurrentProcess?.status]);

    useEffect(() => {
        if (props.successAdding === true && props.CurrentProcess?.status === 3) {
            props.GetAllPractitionerConsumptionsByPractitioner(props.PractitionerId, 1, 100);
        }
    }, [props.successAdding && props.CurrentProcess?.status]);

    // ── No contract associated → keep informational MessageBar ──
    if (!props.haveContractAssociated) {
        return (
            <div className="pcsp-container">
                <MessageBar intent={'warning'} className="pcsp-noContract">
                    <MessageBarBody>
                        <MessageBarTitle>
                            {props.practitioner?.fullName} no se encuentra asignado/a a ningún protocolo
                        </MessageBarTitle>
                        No hay información para sincronizar. Por favor, asigne un protocolo al profesional.{' '}
                        <Link href={'/contracts'}>ir a contratos</Link>
                    </MessageBarBody>
                </MessageBar>
            </div>
        );
    }

    if (!props.PractitionerId) {
        return null;
    }

    const status = props.CurrentProcess?.status;
    const lastSync = formatTimestamp(props.CurrentProcess?.lastSync);

    const descriptor: StatusDescriptor = (() => {
        if (props.isAdding) {
            return {
                variant: 'running',
                eyebrow: 'Sincronización',
                title: 'Generando sincronización…',
                pillLabel: 'En curso',
                timestamp: lastSync,
                actionLabel: 'Sincronizando…',
                actionDisabled: true,
                icon: <Spinner size="extra-tiny" />,
            };
        }
        if (status === 1 || status === 2) {
            return {
                variant: 'running',
                eyebrow: 'Sincronización',
                title: 'Sincronizando consumos del profesional',
                pillLabel: 'En curso',
                timestamp: lastSync,
                actionLabel: 'Sincronizando…',
                actionDisabled: true,
                icon: <ArrowSyncRegular />,
            };
        }
        if (status === 3) {
            return {
                variant: 'success',
                eyebrow: 'Última sincronización',
                title: lastSync ? `Actualizado el ${lastSync}` : 'Sincronización completada',
                pillLabel: 'Al día',
                timestamp: lastSync,
                actionLabel: 'Volver a sincronizar',
                actionDisabled: false,
                icon: <CheckmarkCircleRegular />,
            };
        }
        if (status === 5) {
            return {
                variant: 'error',
                eyebrow: 'Error de sincronización',
                title: lastSync ? `Falló el ${lastSync}` : 'La última sincronización falló',
                pillLabel: 'Error',
                timestamp: lastSync,
                actionLabel: 'Reintentar',
                actionDisabled: false,
                icon: <ArrowSyncDismissRegular />,
            };
        }
        return {
            variant: 'idle',
            eyebrow: 'Sincronización',
            title: 'Sin sincronizaciones previas',
            pillLabel: 'Sin datos',
            timestamp: undefined,
            actionLabel: 'Sincronizar',
            actionDisabled: false,
            icon: <ArrowSyncRegular />,
        };
    })();

    const handleClick = () => {
        if (!descriptor.actionDisabled) {
            props.CreatePractitionerConsumptionSyncProcess(props.PractitionerId);
        }
    };

    return (
        <div className="pcsp-container">
            <div className={`pcsp-card is-${descriptor.variant}`}>
                <div className="pcsp-iconChip">{descriptor.icon}</div>
                <div className="pcsp-textBlock">
                    <span className="pcsp-eyebrow">{descriptor.eyebrow}</span>
                    <span className="pcsp-title" title={descriptor.title}>
                        {descriptor.title}
                    </span>
                    <div className="pcsp-meta">
                        <span className={`pcsp-pill pcsp-pill--${descriptor.variant}`}>
                            {descriptor.pillLabel}
                        </span>
                        {descriptor.timestamp && descriptor.variant === 'running' && (
                            <span className="pcsp-timestamp">
                                <ClockRegular style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                {descriptor.timestamp}
                            </span>
                        )}
                    </div>
                </div>
                <div className="pcsp-action">
                    <button
                        type="button"
                        className={`pcsp-syncBtn pcsp-syncBtn--${descriptor.variant}`}
                        disabled={descriptor.actionDisabled}
                        onClick={handleClick}
                        title={descriptor.actionLabel}
                    >
                        <span className="pcsp-syncBtn-iconWrap">
                            {descriptor.actionDisabled
                                ? <Spinner size="extra-tiny" />
                                : <ArrowSyncRegular />}
                        </span>
                        <span className="pcsp-syncBtn-label">{descriptor.actionLabel}</span>
                    </button>
                </div>
            </div>

            {(props.failureLoading || props.failureAdding) && (
                <MessageBar intent={'error'}>
                    <MessageBarBody>
                        <MessageBarTitle>Error</MessageBarTitle>
                        {props.ErrorAdding?.ErrorMessage || props.ErrorGetting?.ErrorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PractitionerConsumptionSyncProcess as any);
